import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import "./calendarView.scss";
import moment from "moment";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { Days } from "../../../constant/calenderView";
import type { LoadedRange } from "./Appointments";
import type { CaregiverClient } from "./caregiverClientsCache";

import { appointments } from "types/calender/appointments";
import { ButtonDates, Idate } from "types/calender/calenderView";
import { debounce } from "lodash";
import ClientAppointmentBar from "../../../components/clientSupportingComponents/ClientAppointmentBar";
import {
  isClientAppointmentCancelled,
  isClientAppointmentOver,
} from "../../../components/clientSupportingComponents/clientAppointmentStatus";
import { useNavigate } from "react-router-dom";

import {
  CR_FAILURE_STATUS,
  CR_SYNC_AND_FAILURE_STATUS,
  CR_SYNC_STATUS,
  EVENT_STATUS,
} from "constant/AppConstants";

import { myContext } from "hooks/useContext/useContext";
import ClientRescheduleError from "../../../components/sharedComponents/Error/ClientRescheduleError";

interface IProps {
  appointments: appointments[];
  handleDataChange: (startStr: string, endStr: string) => void;
  softloading: boolean;
  patientClinic: number[];
  pollCount: number;
  displayBanner: boolean;
  onContactUs?: () => void;
  // This caregiver's children, built once on the page (Welcome line + chips).
  clients: CaregiverClient[];
  // Range of the latest request once it finished, however it ended.
  loadedRange: LoadedRange | null;
}

const DAY_KEY_FORMAT = "YYYY-MM-DD";
const LOADING_ROWS = 3;
// Re-render on the minute so "Up next" and finished visits stay right even
// when polling has stopped.
const CLOCK_TICK_MS = 60000;
const DAY_TAP_DEBOUNCE_MS = 100;

const isAppointmentLive = (appointment: appointments, now: moment.Moment) =>
  !isClientAppointmentCancelled(appointment) &&
  !appointment?.scheduledStatus?.includes(EVENT_STATUS.canceled) &&
  !isClientAppointmentOver(appointment, now);

const CalendarView: React.FC<IProps> = ({
  appointments,
  handleDataChange,
  softloading,
  patientClinic,
  pollCount,
  displayBanner,
  onContactUs,
  clients,
  loadedRange,
}) => {
  const { prefDate, setPrefDate, globalPath, setGlobalPath } =
    useContext(myContext);
  const navigate = useNavigate();
  const [now, setNow] = useState<number>(() => Date.now());
  const [buttonDates, setButtonDates] = useState<ButtonDates[]>([]);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [activeDay, setActiveDay] = useState<string>(new Date().toString());
  const [sunday, setSunday] = useState<number>(0);
  // null means "All children".
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const buttonDatesRef = useRef<ButtonDates[]>(buttonDates);
  const startDateRef = useRef<string>(startDate);
  const endDateRef = useRef<string>(endDate);

  const calendarRefCustom = useRef<any>(null);
  const [currentDate] = useState<string>(() => new Date().toISOString());
  const [currentDay, setcurrentDay] = useState<any>();
  const [currentMonthYear, setCurrentMonthYear] = useState<any>(
    moment().format("MMMM YYYY")
  );
  const path = "appointments";
  const [RescheduledDate] = useState(
    moment(prefDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
  );
  const [syncStatus, setSyncStatus] = useState<boolean>(false);
  const [prevSyncDataLength, setPrevSyncDataLength] =
    useState<appointments[]>();

  const refdate1 = prefDate
    ? moment(prefDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
    : undefined;

  const { to, from } = globalPath;

  useEffect(() => {
    let syncData: appointments[] = [];
    let timer: NodeJS.Timeout | null = null;
    if (to === "appointments") {
      syncData = appointments.filter(
        (item) =>
          CR_SYNC_AND_FAILURE_STATUS.includes(
            (item.athenaSyncEnabled === true ? item.athenaStatus : 0) ||
              (item.crSyncEnabled === true ? item.crStatus : 0)
          ) && item.startAt === RescheduledDate
      );
    }
    if (
      prevSyncDataLength &&
      CR_SYNC_STATUS.includes(
        (prevSyncDataLength[0]?.athenaSyncEnabled === true
          ? prevSyncDataLength[0]?.athenaStatus
          : 0) ||
          (prevSyncDataLength[0]?.crSyncEnabled === true
            ? prevSyncDataLength[0]?.crStatus
            : 0)
      )
    ) {
      setSyncStatus(false);
    } else if (
      pollCount < 3 &&
      CR_FAILURE_STATUS.includes(
        (syncData[0]?.athenaSyncEnabled === true
          ? syncData[0]?.athenaStatus
          : 0) ||
          (syncData[0]?.crSyncEnabled === true ? syncData[0]?.crStatus : 0)
      )
    ) {
      setSyncStatus(true);
    }
    setPrevSyncDataLength(syncData);

    if (syncStatus === true) {
      timer = setTimeout(() => {
        setSyncStatus(false);
        setGlobalPath(() => ({
          to: undefined,
          from: undefined,
        }));
      }, 15000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [
    appointments,
    pollCount,
    RescheduledDate,
    syncStatus,
    // CR_FAILURE_STATUS,
    // CR_SYNC_AND_FAILURE_STATUS,
    // CR_SYNC_STATUS,
    // globalPath,
    // prevSyncDataLength,
    // setGlobalPath,
  ]);

  const handlePrevButtonClick = () => {
    if (calendarRefCustom.current && calendarRefCustom.current.getApi()) {
      calendarRefCustom.current.getApi().prev();
    }
  };

  const handleNextButtonClick = () => {
    if (calendarRefCustom.current && calendarRefCustom.current.getApi()) {
      calendarRefCustom.current.getApi().next();
    }
  };

  const navigateToCurrentDay = () => {
    if (calendarRefCustom.current && calendarRefCustom.current.getApi()) {
      calendarRefCustom.current.getApi().today();
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const filteredAppointments = useMemo(() => {
    if (!selectedClientId) {
      return appointments;
    }
    return appointments.filter(
      (appointment: appointments) => appointment.client_id === selectedClientId
    );
  }, [appointments, selectedClientId]);

  const seeAllAppts = () => {
    // Calculate the start date of the week containing the activeDay
    const activeDayMoment = moment(activeDay, "ddd MMM DD YYYY");
    const currentWeekStart = activeDayMoment.startOf("week").toISOString();

    setActiveDay(currentWeekStart);

    navigate("/client-appointments", {
      state: {
        prefStartDate: currentWeekStart,
        selectedClientId: selectedClientId,
        uniqueClients: clients.map(({ id, name }) => ({ id, name })),
      },
    });
  };

  // One debounced loader for the component's life, so quick day taps
  // collapse into a single request. It calls the latest handleDataChange, so a
  // new handler (e.g. after a sign-in refresh) never drops a pending tap.
  const handleDataChangeRef = useRef(handleDataChange);
  handleDataChangeRef.current = handleDataChange;
  const debouncehandleDataChange = useMemo(
    () =>
      debounce((startDate: string, endDate: string) => {
        handleDataChangeRef.current(startDate, endDate);
      }, DAY_TAP_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => debouncehandleDataChange.cancel(), [debouncehandleDataChange]);

  const handlePlannerDateViewChange = useCallback(
    (dateArgs: any) => {
      const { startStr, endStr } = dateArgs;
      // A week change replaces any day tap still waiting to load.
      debouncehandleDataChange.cancel();
      const parsedStartData: moment.Moment = moment(startStr);
      parsedStartData.set({ minute: 0, second: 0, millisecond: 0 });
      let start = updateMonthYear(parsedStartData.toDate());
      const parsedEndData: moment.Moment = moment(endStr);
      parsedEndData.set({ minute: 0, second: 0, millisecond: 0 });
      let end = updateMonthYear(parsedEndData.toDate());
      const currentWeek: number = moment().isoWeek();
      const currentDate: any = prefDate
        ? moment(prefDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]").toString()
        : new Date().toString();
      const formattedWeek = parsedEndData.isoWeek();
      const mondayDateOfEveryWeek = prefDate
        ? moment(prefDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
        : moment().week(formattedWeek).day("Monday");

      if (start === end) {
        setCurrentMonthYear(start);
      } else {
        setCurrentMonthYear([(start = start), (end = end)]);
      }

      if (currentWeek !== formattedWeek) {
        setActiveDay(mondayDateOfEveryWeek.toString());
        setcurrentDay(false);
        handleDataChange(
          moment(mondayDateOfEveryWeek).startOf("day").toISOString(),
          moment(mondayDateOfEveryWeek).endOf("day").toISOString()
        );
      } else {
        setActiveDay(currentDate.toString());

        handleDataChange(
          moment(currentDate).startOf("day").toISOString(),
          moment(currentDate).endOf("day").toISOString()
        );
        setcurrentDay(true);
      }
      setStartDate(startStr);
      setEndDate(endStr);
      setPrefDate(undefined);
    },
    [debouncehandleDataChange, handleDataChange, prefDate, setPrefDate]
  );

  useEffect(() => {
    const start: Date = new Date(startDate);
    const end: Date = new Date(endDate);

    const dates: ButtonDates[] = [];
    const dayDifference = Math.floor(
      (end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)
    );

    if (dayDifference <= 7) {
      let dayCount = 0;

      while (start <= end && dayCount < 7) {
        dates.push({ date: new Date(start), week: moment(start).isoWeek() });
        start.setDate(start.getDate() + 1);
        dayCount++;
      }
    } else {
      return;
    }

    setButtonDates(dates);

    if (
      Days[dates[0]?.date.getDay()] === Days[6] &&
      Days[dates[1]?.date.getDay()] === Days[1]
    ) {
      const momentStartDate: moment.Moment = moment(
        dates[0]?.date,
        "YYYY-MM-DD"
      );

      const calculatedWeekNumber: number = momentStartDate.isoWeek();

      setSunday(calculatedWeekNumber);
    } else {
      const momentStartDate: moment.Moment = moment(
        dates[1]?.date,
        "YYYY-MM-DD"
      );

      const calculatedWeekNumber: number = momentStartDate.isoWeek();

      setSunday(calculatedWeekNumber);
    }
  }, [startDate, endDate, sunday]);

  useEffect(() => {
    buttonDatesRef.current = buttonDates;
    startDateRef.current = startDate;
    endDateRef.current = endDate;
  }, [buttonDates, startDate, endDate, sunday]);

  useEffect(() => {
    const currentWeek: number = moment().isoWeek();
    const currentDate: string = prefDate
      ? moment(prefDate).toISOString()
      : new Date().toString();
    const buttonDates = buttonDatesRef.current;
    if (currentWeek === buttonDates[3]?.week) {
      setActiveDay(currentDate.toString());
    }
  }, [prefDate]);

  const handleButtonClick = (date: Idate) => {
    const currentDateButton = moment(currentDate).startOf("day").toISOString();
    const dateButtonStart = moment(date.date).startOf("day").toISOString();
    const dateButtonEnd = moment(date.date).endOf("day").toISOString();

    debouncehandleDataChange(dateButtonStart, dateButtonEnd);
    setActiveDay(date?.date.toString());
    if (moment(dateButtonStart).isSame(currentDateButton)) {
      setcurrentDay(true);
    } else {
      setcurrentDay(false);
    }
  };

  const handleTodayButtonClick = useCallback(() => {
    // Today replaces any day tap still waiting to load.
    debouncehandleDataChange.cancel();
    const currentDate: Date = new Date();
    if (new Date(activeDay).getDate() !== currentDate.getDate()) {
      handleDataChange(
        moment(currentDate).startOf("day").toISOString(),
        moment(currentDate).endOf("day").toISOString()
      );
      setActiveDay(new Date().toString());
    }
    setcurrentDay(true);
  }, [debouncehandleDataChange, handleDataChange, activeDay]);

  const updateMonthYear = (date: Date) => {
    return moment(date).format("MMMM YYYY");
  };

  const getMonthAndYear = (): any => {
    const [firstMonth, firstYear] = currentMonthYear[0].split(" ");
    const [secondMonth, secondYear] = currentMonthYear[1].split(" ");

    if (firstYear === secondYear)
      return `${firstMonth} - ${secondMonth} ${firstYear}`;

    return `${firstMonth} ${firstYear} - ${secondMonth} ${secondYear}`;
  };

  // Within the current week, load today directly. From another week, moving the
  // hidden calendar back fires datesSet, which already loads today.
  const handleTodayClick = () => {
    const showingCurrentWeek = buttonDates.some((date) =>
      moment(date.date).isSame(moment(), "day")
    );
    if (showingCurrentWeek) {
      handleTodayButtonClick();
    } else {
      navigateToCurrentDay();
    }
  };

  const isActiveDate = (date: Date) =>
    moment(activeDay, [
      "YYYY-MM-DDTHH:mm:ss.SSS[Z]", // Format for scenario 2
      "ddd MMM DD YYYY", // Format for scenario 1
    ]).isValid() &&
    moment(activeDay).format(DAY_KEY_FORMAT) ===
      moment(date).startOf("day").format(DAY_KEY_FORMAT);

  const today = moment(now);
  const activeMoment = moment(activeDay);
  // Rows show only once the latest finished request was for exactly this
  // day; until then (day change, first load, slow token) it's placeholders.
  const isLoadingDay =
    softloading ||
    !loadedRange ||
    !moment(loadedRange.start).isSame(activeMoment, "day") ||
    !moment(loadedRange.end).isSame(activeMoment, "day");
  const isActiveToday = activeMoment.isSame(today, "day");
  const isActiveTomorrow = activeMoment.isSame(
    moment(today).add(1, "day"),
    "day"
  );
  const dayLabel = isActiveToday
    ? "Today"
    : isActiveTomorrow
    ? "Tomorrow"
    : activeMoment.format("dddd");
  const tomorrowButton = buttonDates.find((date) =>
    moment(date.date).isSame(moment(today).add(1, "day"), "day")
  );

  const upNextAppointment: appointments | undefined = isActiveToday
    ? filteredAppointments.find((appointment: appointments) => isAppointmentLive(appointment, today))
    : undefined;
  const isTodayDone =
    isActiveToday &&
    !isLoadingDay &&
    !filteredAppointments.some((appointment: appointments) => isAppointmentLive(appointment, today));

  const monthLabel =
    typeof currentMonthYear === "string" ? currentMonthYear : getMonthAndYear();

  return (
    <Paper
      component="section"
      aria-labelledby="schedule-heading"
      sx={{ p: 3 }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" component="h2" id="schedule-heading">
            Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {monthLabel}
          </Typography>
        </Box>
        <Button
          onClick={handleTodayClick}
          disabled={currentDay || softloading}
          sx={{ minHeight: 44 }}
        >
          Today
        </Button>
      </Stack>

      {(syncStatus || displayBanner) && (
        <Box sx={{ mb: 2 }}>
          <ClientRescheduleError
            from={from}
            date={prefDate}
            syncStatus={syncStatus}
            displayBanner={displayBanner}
            onContactUs={onContactUs}
          />
        </Box>
      )}

      {clients.length > 1 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 2 }}
          role="group"
          aria-label="Show appointments for"
        >
          <Chip
            label="All children"
            onClick={() => setSelectedClientId(null)}
            color={selectedClientId === null ? "primary" : "default"}
            variant={selectedClientId === null ? "filled" : "outlined"}
            aria-pressed={selectedClientId === null}
            sx={{ height: 44, borderRadius: 22, px: 0.5 }}
          />
          {clients.map((client) => (
            <Chip
              key={client.id}
              label={client.name}
              onClick={() => setSelectedClientId(client.id)}
              color={selectedClientId === client.id ? "primary" : "default"}
              variant={selectedClientId === client.id ? "filled" : "outlined"}
              aria-pressed={selectedClientId === client.id}
              sx={{ height: 44, borderRadius: 22, px: 0.5 }}
            />
          ))}
        </Stack>
      )}

      <Stack
        direction="row"
        alignItems="center"
        spacing={{ xs: 0, sm: 0.5 }}
        sx={{ mb: 1 }}
        role="group"
        aria-label="Choose a day"
      >
        <IconButton
          onClick={handlePrevButtonClick}
          disabled={softloading}
          aria-label="Previous week"
          sx={{ width: { xs: 36, sm: 44 }, height: 44, flexShrink: 0 }}
        >
          <ChevronLeftIcon />
        </IconButton>
        {buttonDates.map((date) => {
          const isActive = isActiveDate(date.date);
          const isToday = moment(date.date).isSame(today, "day");
          return (
            <ButtonBase
              key={date.date.toISOString()}
              onClick={() => handleButtonClick(date)}
              disabled={softloading}
              aria-pressed={isActive}
              aria-label={moment(date.date).format("dddd, MMMM D")}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 52,
                borderRadius: 2,
                flexDirection: "column",
                typography: "caption",
                color: isActive ? "primary.contrastText" : "text.secondary",
                bgcolor: isActive ? "primary.main" : "transparent",
                "&:hover": { bgcolor: isActive ? "primary.main" : "action.hover" },
              }}
            >
              {Days[date.date.getDay()]}
              <Box
                component="span"
                sx={{
                  typography: "subtitle1",
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: isActive
                    ? "primary.contrastText"
                    : isToday
                    ? "primary.main"
                    : "text.primary",
                }}
              >
                {date.date.getDate()}
              </Box>
            </ButtonBase>
          );
        })}
        <IconButton
          onClick={handleNextButtonClick}
          disabled={softloading}
          aria-label="Next week"
          sx={{ width: { xs: 36, sm: 44 }, height: 44, flexShrink: 0 }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={1}
        sx={{ pt: 1.5, pb: 0.5, borderTop: 1, borderColor: "divider" }}
      >
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 500 }}>
          {dayLabel}, {activeMoment.format("MMMM D")}
        </Typography>
        {!isLoadingDay && (
          <Typography variant="caption" color="text.secondary">
            {filteredAppointments.length}{" "}
            {filteredAppointments.length === 1 ? "appointment" : "appointments"}
          </Typography>
        )}
      </Stack>

      {isLoadingDay ? (
        <Stack
          spacing={1}
          sx={{ py: 1 }}
          role="status"
          aria-busy="true"
          aria-label="Loading appointments"
        >
          {Array.from({ length: LOADING_ROWS }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={64} />
          ))}
        </Stack>
      ) : (
        <>
          {filteredAppointments.length > 0 && (
            <Box sx={{ "& > * + *": { borderTop: 1, borderColor: "divider" } }}>
              {filteredAppointments.map((appointment: any) => (
                <ClientAppointmentBar
                  key={appointment.id}
                  appointment={appointment}
                  patientClinic={patientClinic}
                  activeDay={activeDay?.toString() || ""}
                  path={path}
                  displayBanner={displayBanner}
                  pollCount={pollCount}
                  isUpNext={appointment === upNextAppointment}
                  now={now}
                  onContactUs={onContactUs}
                />
              ))}
            </Box>
          )}
          {isTodayDone ? (
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={1.5}
              sx={{ pt: 2, pb: 1 }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                {filteredAppointments.length > 0
                  ? "No more appointments today"
                  : "No appointments today"}
              </Typography>
              {tomorrowButton && (
                <Button
                  variant="outlined"
                  onClick={() => handleButtonClick(tomorrowButton)}
                  sx={{ minHeight: 44 }}
                >
                  See tomorrow
                </Button>
              )}
            </Stack>
          ) : (
            filteredAppointments.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ pt: 2, pb: 1 }}>
                No appointments on {activeMoment.format("dddd")}.
              </Typography>
            )
          )}
        </>
      )}

      <Stack
        alignItems="center"
        sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}
      >
        <Button
          startIcon={<CalendarTodayOutlinedIcon />}
          onClick={seeAllAppts}
          sx={{ minHeight: 44 }}
        >
          See the full week
        </Button>
      </Stack>

      {/* Hidden: still drives week navigation (prev/next/today) and the
          date ranges handed to handleDataChange. */}
      <Box sx={{ display: "none" }} aria-hidden="true">
        <FullCalendar
          ref={calendarRefCustom as React.MutableRefObject<FullCalendar>}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          allDaySlot={false}
          initialView="timeGridCustomView"
          views={{
            timeGridCustomView: {
              type: "timeGrid",
              duration: { weeks: 1 },
            },
          }}
          headerToolbar={false}
          datesSet={handlePlannerDateViewChange}
          initialDate={refdate1}
        />
      </Box>
    </Paper>
  );
};

export default React.memo(CalendarView);
